/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import React from "react"
import dayjs from "dayjs"
import { jsx, css } from "@emotion/core"
import { Dropdown, Button } from "@duxton-web/ui"
import {
  MoreHorizontalUniversal,
  RealtimeFeedbackOutlined,
} from "@duxton-web/icons"
import { MenuProps } from "@duxton-web/ui/es/menu"
import { Review, ReviewUnReportableReason } from "types/api/menuFeedback.type"
import { ActionType } from "types/models/feedback"
import { useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { actions as feedbackDrawerActions } from "store/duck/feedbackDrawer"
import { getActionSuccess, getActionFail } from "store/duck/utils/actionUtils"
import { isEmpty } from "lodash"
import { useTranslationsMap } from "common/utils/i18n"
import { TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventCategory,
  EventCategoryEng,
  EventActionEng,
} from "constants/tracking"

import { canSubmitReport } from "pages/feedback/utils"
import { ReviewThrottleResponse } from "store/duck/feedback.type"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { usePostMerchantReviewThrottleMutation } from "queries/feedbackApi"

type ReviewActionColumnProp = {
  review: Review
  onClick(actionType: ActionType, review: Review): void
  reviewThrottle?: ReviewThrottleResponse
}

export enum ReviewAction {
  REPORT = "REPORT",
}

const reportStyle = css({
  padding: "12px !important",
  display: "flex",
  gap: "8px",
  alignItems: "center",
  width: "250px",
})

const ReviewActionColumn: React.FC<ReviewActionColumnProp> = ({
  review,
  onClick,
  reviewThrottle,
}) => {
  const dispatch = useDispatch()

  const textMap = useTranslationsMap({
    report: "feedback.cta.report",
    reviewReported: "feedback.helperText.reported",
  })

  const isReported =
    review.unreportableReason ===
    ReviewUnReportableReason.ReviewUnreportableReasonReviewIsReported

  const {
    flags: {
      postMerchantReviewThrottle: isPostMerchantReviewThrottleRtkEnabled,
    },
    isLoading: isLoadingRtkQueryMigrationEndpointsFlag,
  } = useRtkQueryMigrationEndpointsFlag()

  const [
    postMerchantReviewThrottleRtk,
  ] = usePostMerchantReviewThrottleMutation()

  // Check if the reviewThrottle value is already available for specific merchantID in the state and not using old day value.
  const existingValue =
    reviewThrottle &&
    reviewThrottle.merchantID === review.merchantID &&
    reviewThrottle.fetchHour === dayjs().hour()

  const handleMenuClick = ({ key }: any) => {
    switch (key) {
      case ReviewAction.REPORT:
        // Calling new endpoint to check either the merchant is allowing to report or not
        if (review && !existingValue) {
          if (isPostMerchantReviewThrottleRtkEnabled) {
            // RTK Query path
            TroyWebTracker.sendEvent({
              category: EventCategoryEng.rtkMigration,
              action: EventActionEng.postMerchantReviewThrottle,
              labels: {
                MERCHANT_ID: review.merchantID,
                IS_RTK_ENABLED: isPostMerchantReviewThrottleRtkEnabled,
                FROM_RTK_QUERY: true,
                FROM_OLD_REDUX: false,
                ACTION_NAME: "postMerchantReviewThrottle",
              },
            })
            // Dispatch request action to set loading state
            dispatch({
              type: feedbackDrawerActions.POST_MERCHANT_THROTTLE,
            })
            postMerchantReviewThrottleRtk({
              merchantID: review.merchantID,
            })
              .unwrap()
              .then(data => {
                // Sync to legacy Redux state for backward compatibility
                dispatch({
                  type: getActionSuccess(
                    feedbackDrawerActions.POST_MERCHANT_THROTTLE,
                  ),
                  payload: {
                    data,
                    headers: {
                      date: new Date().toISOString(),
                    },
                  },
                  meta: {
                    previousAction: {
                      merchantID: review.merchantID,
                    },
                  },
                })
              })
              .catch(() => {
                // Dispatch fail action to clear loading state
                dispatch({
                  type: getActionFail(
                    feedbackDrawerActions.POST_MERCHANT_THROTTLE,
                  ),
                })
              })
          } else {
            // Old Redux path
            TroyWebTracker.sendEvent({
              category: EventCategoryEng.rtkMigration,
              action: EventActionEng.postMerchantReviewThrottle,
              labels: {
                MERCHANT_ID: review.merchantID,
                IS_RTK_ENABLED: isPostMerchantReviewThrottleRtkEnabled,
                FROM_RTK_QUERY: false,
                FROM_OLD_REDUX: true,
                ACTION_NAME: "postMerchantReviewThrottle",
              },
            })
            dispatch(
              actionCreator.feedbackDrawer.postMerchantReviewThrottle(
                review.merchantID,
              ),
            )
          }
        }
        TroyWebTracker.sendEvent({
          category: EventCategory.feedbackHomePage,
          action: EventAction.clickReport,
          labels: {
            REVIEW_ID: review?.reviewID,
            REVIEW_RATING: review?.rating,
          },
        })
        if (isReported) {
          onClick(ActionType.SEE_DETAILS, review)
          break
        }
        dispatch(
          actionCreator.feedbackDrawer.toggleReport({
            review,
            visible: true,
          }),
        )
        break
      default:
    }
  }

  const isDisabled =
    !canSubmitReport(review) || isLoadingRtkQueryMigrationEndpointsFlag
  const actions: MenuProps["items"] = [
    {
      label: (
        <span
          css={reportStyle}
          style={{ color: isDisabled ? "inherit" : "#f55538" }}
        >
          {" "}
          <RealtimeFeedbackOutlined
            style={{ color: "#f55538", fontSize: 18 }}
          />{" "}
          {textMap.report}{" "}
        </span>
      ),
      key: ReviewAction.REPORT,
      disabled: isDisabled,
    },
  ]

  return (
    <div onClick={e => e?.stopPropagation()}>
      {!isEmpty(actions) && (
        <Dropdown menu={{ items: actions, onClick: handleMenuClick }}>
          <Button shape="default">
            <MoreHorizontalUniversal />
          </Button>
        </Dropdown>
      )}
    </div>
  )
}

export default React.memo(ReviewActionColumn)
